import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaEdit, FaSpinner, FaImage, FaTimes, FaPlus, FaTrash } from 'react-icons/fa';
import { aboutAPI } from '../../utils/api.js';
import { uploadImage } from '../../utils/uploadImage';

const AboutEditor = () => {
  const [about, setAbout] = useState({ title: '', content: '', image_url: '', tagline: '', roles: [], extra_content: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingRoleIdx, setEditingRoleIdx] = useState(null);

  useEffect(() => {
    loadAbout();
  }, []);

  const loadAbout = async () => {
    try {
      setIsLoading(true);
      const response = await aboutAPI.get();
      if (response.success && response.data) {
        setAbout(response.data);
      }
    } catch (error) {
      setMessage('Error loading about content');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    try {
      const url = await uploadImage(file, 'about');
      setAbout(prev => ({ ...prev, image_url: url }));
      setMessage('Image uploaded successfully!');
    } catch (error) {
      setMessage('Error uploading image');
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setMessage('');
      const response = await aboutAPI.update(about);
      if (response.success) {
        setMessage('About content updated successfully!');
        setIsEditing(false);
        loadAbout();
      }
    } catch (error) {
      setMessage('Error saving about content');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    loadAbout();
    setMessage('');
    setEditingRoleIdx(null);
  };

  // Role editing helpers
  const handleRoleChange = (idx, field, value) => {
    setAbout(prev => ({
      ...prev,
      roles: prev.roles.map((r, i) => i === idx ? { ...r, [field]: value } : r)
    }));
  };
  const handleAddRole = () => {
    setAbout(prev => ({ ...prev, roles: [...(prev.roles || []), { title: '', description: '', icon: '' }] }));
    setEditingRoleIdx((about.roles || []).length);
  };
  const handleDeleteRole = (idx) => {
    setAbout(prev => ({ ...prev, roles: prev.roles.filter((_, i) => i !== idx) }));
    setEditingRoleIdx(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-900 rounded-2xl p-8 shadow-lg max-w-2xl mx-auto relative"
    >
      <h3 className="text-2xl font-bold text-primary mb-6 text-center">About Section Editor</h3>
      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-4 p-3 rounded-lg ${message.includes('Error') ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-green-500/20 text-green-300 border border-green-500/30'}`}
        >
          {message}
        </motion.div>
      )}
      {/* Main Info */}
      <div className="mb-8">
        <h4 className="text-lg font-semibold text-white mb-4">Main Info</h4>
        <div className="flex items-center gap-6 mb-4">
          {about.image_url && (
            <img src={about.image_url} alt="Preview" className="w-24 h-24 rounded-full border-2 border-primary object-cover" />
          )}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Upload Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={e => {
                const file = e.target.files[0];
                if (file) handleImageUpload(file);
              }}
              className="block mt-1 text-sm text-gray-400"
            />
          </div>
        </div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
        <input
          type="text"
          value={about.title}
          onChange={e => setAbout(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 mb-4 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Enter about section title..."
        />
        <label className="block text-sm font-medium text-gray-300 mb-1">Tagline</label>
        <input
          type="text"
          value={about.tagline}
          onChange={e => setAbout(prev => ({ ...prev, tagline: e.target.value }))}
          className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 mb-4 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Enter tagline..."
        />
        <label className="block text-sm font-medium text-gray-300 mb-1">Content</label>
        <textarea
          value={about.content}
          onChange={e => setAbout(prev => ({ ...prev, content: e.target.value }))}
          rows={5}
          className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 mb-4 focus:outline-none focus:ring-2 focus:ring-primary resize-vertical"
          placeholder="Enter about section content..."
        />
      </div>
      {/* Roles */}
      <div className="mb-8">
        <h4 className="text-lg font-semibold text-white mb-4">Roles</h4>
        <div className="space-y-4">
          {(about.roles || []).map((role, idx) => (
            <div key={idx} className="bg-gray-800 border border-gray-700 rounded-lg p-4 flex flex-col gap-2 relative">
              {editingRoleIdx === idx ? (
                <>
                  <label className="block text-xs text-gray-400">Icon (e.g. FaRobot)</label>
                  <input
                    type="text"
                    value={role.icon}
                    onChange={e => handleRoleChange(idx, 'icon', e.target.value)}
                    className="w-full px-2 py-1 rounded bg-gray-900 text-white border border-gray-700 mb-2"
                    placeholder="Icon name (e.g. FaRobot)"
                  />
                  <label className="block text-xs text-gray-400">Title</label>
                  <input
                    type="text"
                    value={role.title}
                    onChange={e => handleRoleChange(idx, 'title', e.target.value)}
                    className="w-full px-2 py-1 rounded bg-gray-900 text-white border border-gray-700 mb-2"
                    placeholder="Role title"
                  />
                  <label className="block text-xs text-gray-400">Description</label>
                  <input
                    type="text"
                    value={role.description}
                    onChange={e => handleRoleChange(idx, 'description', e.target.value)}
                    className="w-full px-2 py-1 rounded bg-gray-900 text-white border border-gray-700 mb-2"
                    placeholder="Role description"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setEditingRoleIdx(null)}
                      className="px-3 py-1 bg-green-700 text-white rounded hover:bg-green-800"
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteRole(idx)}
                      className="px-3 py-1 bg-red-700 text-white rounded hover:bg-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-white">{role.title}</div>
                    <div className="text-gray-400 text-sm">{role.description}</div>
                    <div className="text-xs text-gray-500">{role.icon}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingRoleIdx(idx)}
                    className="px-3 py-1 bg-blue-700 text-white rounded hover:bg-blue-800"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddRole}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark mt-2"
          >
            <FaPlus /> Add Role
          </button>
        </div>
      </div>
      {/* Additional Content */}
      <div className="mb-8">
        <h4 className="text-lg font-semibold text-white mb-4">Additional Content</h4>
        <textarea
          value={about.extra_content || ''}
          onChange={e => setAbout(prev => ({ ...prev, extra_content: e.target.value }))}
          rows={4}
          className="w-full px-3 py-2 rounded-lg bg-gray-800 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-primary resize-vertical"
          placeholder="Enter any additional content (optional)..."
        />
      </div>
      {/* Save/Cancel Buttons */}
      <div className="flex justify-end gap-4 sticky bottom-0 bg-gray-900 pb-2 pt-4 z-10">
        <button
          type="button"
          onClick={handleCancel}
          className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="px-6 py-2 bg-primary text-white rounded-lg flex items-center gap-2 hover:bg-primary-dark disabled:opacity-50"
          disabled={isSaving}
        >
          {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </motion.div>
  );
};

export default AboutEditor; 